import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Box,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "./shared";
import { RadioOption } from "./CreationWizard/FormComponents";
import { getAllBackgroundOptions, convertAssetPathToUrl, getSystemAssetThumbnail } from "../config/assets";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { api } from "../services/api";
import TemplateBlankChips from "./TemplateBlankChips";

export interface Template {
  id: string; // Frontend uses 'id', backend uses 'templateId' - we'll map it
  templateId?: string; // Backend field
  name: string;
  title: string;
  description: string;
  background: string;
  noticeType: "info" | "custom_event" | "image_notice";
  isUrgent?: boolean;
  audience?: string;
  targetSubcircle?: string;
  createdAt: string;
  usageCount?: number; // Number of times this template has been used
  organizationId?: string; // Backend field - used to identify system templates
  isPremade?: boolean;
}

const BLANK_HINT =
  "Add a configurable value to the title or description for anything that changes each time, like a date.";

/** The fields a Blank chip can insert into. */
type BlankField = "title" | "description";

const NOTICE_TYPE_CHOICES = [
  {
    value: "info",
    label: "Information",
    description: "General information or updates",
  },
  {
    value: "custom_event",
    label: "Event",
    description: "Event with date, time, and location",
  },
] as const;

type TemplateNoticeType = (typeof NOTICE_TYPE_CHOICES)[number]["value"];

interface TemplateCreationModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (template: Template) => void;
  templateToEdit?: Template | null; // Template to edit, null for new template
  disableAudienceSelection?: boolean; // If true, hide audience selection (for system templates)
  isSystemTemplate?: boolean; // If true, creates template with organizationId="SYSTEM"
}

const TemplateCreationModal: React.FC<TemplateCreationModalProps> = ({
  open,
  onClose,
  onSave,
  templateToEdit,
  disableAudienceSelection = false,
  isSystemTemplate = false,
}) => {
  const isEditMode = !!templateToEdit;
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [background, setBackground] = useState("");
  const [noticeType, setNoticeType] = useState<TemplateNoticeType>("info");
  const [isUrgent, setIsUrgent] = useState(false);
  const [audience, setAudience] = useState<string>("");
  const [targetSubcircle, setTargetSubcircle] = useState<string>("");
  const [allSubcircles, setAllSubcircles] = useState<Array<{
    circleId: string;
    name: string;
    description?: string;
  }>>([]);
  const [loadingSubcircles, setLoadingSubcircles] = useState(false);
  const { user, token } = useAuth();
  const { showMessage } = useSnackbar();
  const [errors, setErrors] = useState<{
    name?: string;
    title?: string;
  }>({});

  const backgrounds = getAllBackgroundOptions(user?.schoolType);

  // Blank chips insert at the cursor of whichever field was used last.
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);
  const [lastBlankField, setLastBlankField] = useState<BlankField>("title");
  const [cursorAfterInsert, setCursorAfterInsert] = useState<{
    field: BlankField;
    position: number;
  } | null>(null);

  const handleInsertBlank = (token: string) => {
    const [value, setValue, input] =
      lastBlankField === "title"
        ? [title, setTitle, titleInputRef.current]
        : [description, setDescription, descriptionInputRef.current];
    // After any selected text rather than over it: tabbing into a field
    // selects all of it, and a chip shouldn't wipe the title.
    const at = input?.selectionEnd ?? value.length;
    setValue(value.slice(0, at) + token + value.slice(at));
    setCursorAfterInsert({ field: lastBlankField, position: at + token.length });
  };

  // Keep the cursor after the inserted Blank, so the next one follows it.
  useLayoutEffect(() => {
    if (!cursorAfterInsert) return;
    const input =
      cursorAfterInsert.field === "title"
        ? titleInputRef.current
        : descriptionInputRef.current;
    input?.setSelectionRange(
      cursorAfterInsert.position,
      cursorAfterInsert.position,
    );
    setCursorAfterInsert(null);
  }, [cursorAfterInsert]);

  // Fetch subcircles for audience selection
  useEffect(() => {
    const fetchSubcircles = async () => {
      if (!user?.schoolId || !token) return;

      try {
        setLoadingSubcircles(true);
        const data = await api.circles.getAll(user.schoolId, token);
        const fetchedSubcircles = (data.circles || []).filter(
          (circle: any) => circle.isSubCircle
        );
        setAllSubcircles(fetchedSubcircles);
      } catch (err) {
        console.error("Error fetching subcircles:", err);
      } finally {
        setLoadingSubcircles(false);
      }
    };

    if (open) {
      fetchSubcircles();
    }
  }, [user?.schoolId, token, open]);

  // Populate form when editing
  useEffect(() => {
    if (templateToEdit && open) {
      setName(templateToEdit.name);
      setTitle(templateToEdit.title);
      setDescription(templateToEdit.description);
      setBackground(templateToEdit.background || "");
      setNoticeType(
        templateToEdit.noticeType === "custom_event" ? "custom_event" : "info"
      );
      setIsUrgent(templateToEdit.isUrgent || false);
      setAudience(templateToEdit.audience || "");
      setTargetSubcircle(templateToEdit.targetSubcircle || "");
      setLastBlankField("title");
    } else if (!templateToEdit && open) {
      // Reset form for new template
      setName("");
      setTitle("");
      setDescription("");
      setBackground("");
      setNoticeType("info");
      setIsUrgent(false);
      setAudience("");
      setTargetSubcircle("");
      setLastBlankField("title");
    }
  }, [templateToEdit, open]);

  const handleAudienceChange = (value: string) => {
    if (value === "circle") {
      setAudience("circle");
      setTargetSubcircle("");
    } else {
      setAudience("subcircle");
      setTargetSubcircle(value);
    }
  };

  const handleSave = async () => {
    const newErrors: { name?: string; title?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = "Template name is required";
    }
    
    if (!title.trim()) {
      newErrors.title = "Notice title is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    if (!token) {
      console.error("No authentication token available");
      return;
    }

    try {
      if (isEditMode && templateToEdit) {
        // Update existing template
        const templateId = templateToEdit.templateId || templateToEdit.id;
        const updateData: any = {
          name: name.trim(),
          title: title.trim(),
          description: description.trim(),
          background: background ? convertAssetPathToUrl(background) : "",
          noticeType,
        };

        if (noticeType === "info") {
          updateData.isUrgent = isUrgent;
        }

        if (!disableAudienceSelection && audience) {
          updateData.audience = audience;
          if (audience === "subcircle" && targetSubcircle) {
            updateData.targetSubcircle = targetSubcircle;
          }
        }

        const response = await api.templates.update(templateId, updateData, token);
        
        // Map backend response to frontend format
        const template: Template = {
          id: response.template.templateId,
          templateId: response.template.templateId,
          name: response.template.name,
          title: response.template.title,
          description: response.template.description,
          background: response.template.background,
          noticeType: response.template.noticeType,
          isUrgent: response.template.isUrgent,
          audience: response.template.audience,
          targetSubcircle: response.template.targetSubcircle,
          createdAt: response.template.createdAt,
          usageCount: response.template.usageCount,
        };
        
        if (onSave) {
          onSave(template);
        }
      } else {
        // Create new template
        const createData = {
          name: name.trim(),
          title: title.trim(),
          description: description.trim(),
          background: background ? (convertAssetPathToUrl(background) || "") : "",
          noticeType,
          isUrgent: noticeType === "info" ? isUrgent : undefined,
          audience: disableAudienceSelection ? undefined : (audience || undefined),
          targetSubcircle: disableAudienceSelection ? undefined : (audience === "subcircle" ? targetSubcircle : undefined),
          isSystemTemplate: isSystemTemplate, // Only set when creating from system template page
        };

        const response = await api.templates.create(createData, token);
        
        // Map backend response to frontend format
        const template: Template = {
          id: response.template.templateId,
          templateId: response.template.templateId,
          name: response.template.name,
          title: response.template.title,
          description: response.template.description,
          background: response.template.background,
          noticeType: response.template.noticeType,
          isUrgent: response.template.isUrgent,
          audience: response.template.audience,
          targetSubcircle: response.template.targetSubcircle,
          createdAt: response.template.createdAt,
          usageCount: response.template.usageCount,
        };
        
        if (onSave) {
          onSave(template);
        }
      }

      // Reset form
      setName("");
      setTitle("");
      setDescription("");
      setBackground("");
      setNoticeType("info");
      setIsUrgent(false);
      setAudience("");
      setTargetSubcircle("");
      setErrors({});
      onClose();
    } catch (error: any) {
      console.error("Error saving template:", error);
      showMessage({
        message: "Couldn't save template. Please try again.",
        severity: "error",
      });
    }
  };

  const handleClose = () => {
    // Don't reset form here - let useEffect handle it based on templateToEdit
    setErrors({});
    onClose();
  };

  return (
    <ActionModal
      open={open}
      onClose={handleClose}
      title={isEditMode ? "Edit template" : "Create template"}
      subtitle="Save a notice you send often so you can start from it next time."
      maxWidth="sm"
      footer={
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
          <NoticeboardButton variant="secondary" size={ACTION_MODAL_FOOTER_PRIMARY_SIZE} onClick={handleClose}>
            Cancel
          </NoticeboardButton>
          <NoticeboardButton variant="primary" size={ACTION_MODAL_FOOTER_PRIMARY_SIZE} onClick={handleSave}>
            {isEditMode ? "Save changes" : "Save template"}
          </NoticeboardButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          label="Template name"
          required
          fullWidth
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          placeholder="e.g. Photo day"
        />

        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ mb: 1 }}>
            Notice type
          </FormLabel>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 1.5,
            }}
          >
            {NOTICE_TYPE_CHOICES.map((choice) => (
              <RadioOption
                key={choice.value}
                isSelected={noticeType === choice.value}
              >
                <input
                  type="radio"
                  name="noticeType"
                  value={choice.value}
                  checked={noticeType === choice.value}
                  onChange={() => setNoticeType(choice.value)}
                />
                <div className="radio-circle" />
                <div className="label-text">{choice.label}</div>
                <div className="description">{choice.description}</div>
              </RadioOption>
            ))}
          </Box>
        </FormControl>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {BLANK_HINT}
          </Typography>
          <TemplateBlankChips onInsert={handleInsertBlank} />
        </Box>

        <TextField
          label="Notice title"
          required
          fullWidth
          size="small"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onFocus={() => setLastBlankField("title")}
          inputRef={titleInputRef}
          error={!!errors.title}
          helperText={errors.title}
          placeholder="e.g. Annual school photographs"
        />

        <TextField
          label="Description"
          fullWidth
          size="small"
          multiline
          minRows={4}
          maxRows={8}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onFocus={() => setLastBlankField("description")}
          inputRef={descriptionInputRef}
          inputProps={{ maxLength: 1000 }}
        />

        {noticeType === "info" && (
          <FormControlLabel
            control={
              <Switch
                checked={isUrgent}
                onChange={(e) => setIsUrgent(e.target.checked)}
                color="primary"
              />
            }
            label="Mark as Urgent"
          />
        )}

        {!disableAudienceSelection && (
          <FormControl fullWidth size="small">
            <InputLabel id="template-audience-label">Audience (optional)</InputLabel>
            <Select
              labelId="template-audience-label"
              value={audience === "circle" ? "circle" : targetSubcircle || ""}
              label="Audience (optional)"
              onChange={(e) => handleAudienceChange(e.target.value)}
              disabled={loadingSubcircles}
            >
              <MenuItem value="circle">Entire Circle</MenuItem>
              {allSubcircles.map((subcircle) => (
                <MenuItem key={subcircle.circleId} value={subcircle.circleId}>
                  {subcircle.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ mb: 1 }}>
            Background
          </FormLabel>
          <Box
            sx={{
              display: { xs: "flex", sm: "grid" },
              gridTemplateColumns: { sm: "repeat(3, minmax(0, 1fr))" },
              overflowX: { xs: "auto", sm: "visible" },
              WebkitOverflowScrolling: "touch",
              gap: { xs: 1, sm: 1.5 },
              pb: { xs: 0.5, sm: 0 },
            }}
          >
            {backgrounds.map((bg) => {
              const isSelected = background === bg.value;
              return (
                <Box
                  key={bg.value}
                  onClick={() => setBackground(bg.value)}
                  sx={{
                    position: "relative",
                    flexShrink: 0,
                    width: { xs: 96, sm: "auto" },
                    p: 1,
                    border: isSelected ? 2 : 1,
                    borderColor: isSelected ? "primary.main" : "divider",
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "border-color 0.2s",
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  {bg.value ? (
                    <Box
                      component="img"
                      src={getSystemAssetThumbnail(bg.value)}
                      alt={bg.label}
                      loading="lazy"
                      decoding="async"
                      sx={{
                        width: "100%",
                        height: { xs: 72, sm: 80 },
                        objectFit: "cover",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: { xs: 72, sm: 80 },
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: "grey.50",
                        border: 1,
                        borderStyle: "dashed",
                        borderColor: "grey.300",
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: "0.72rem", sm: "0.875rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    {bg.label}
                  </Box>
                  {isSelected && (
                    <Box
                      aria-hidden
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "common.white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 12 }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </FormControl>
      </Box>
    </ActionModal>
  );
};

export default TemplateCreationModal;
